import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useDevice } from "@hooks/useDevice";
import Box from "@memorang/ui/src/components/Box";
import { fetchExamDetails } from "api/commerce/exam-service";
import type { AccessCodeData } from "api/commerce/types";
import ProductIcon from "components/ProductIcon";
import { useRouter } from "expo-router";
import { useState } from "react";
import { Button, Card, List, Text } from "react-native-paper";
import { events, useRedeemCopilotAnalytics } from "../analytics";
import { formatCase } from "../helpers";

export type AccessContentProps = {
	success: boolean;
	data?: AccessCodeData;
	productData?: AccessCodeData[];
};

export const AccessContent = ({
	success,
	data,
	productData,
}: AccessContentProps) => {
	const router = useRouter();
	const { isMobile } = useDevice();

	const { trackRedeemCopilotEvent } = useRedeemCopilotAnalytics();

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const { handleSwitchExamContext } = useExamContext();

	const items = (success ? productData || [] : [data]).filter(
		(item) => item != null,
	);

	if (!items || items.length === 0) {
		return;
	}

	const handleAccessContent = async (item: AccessCodeData) => {
		trackRedeemCopilotEvent(events.accessContentInitiated, {
			type: "success",
			message: "Access content initiated",
			switchToExamId: item.examId,
			switchToExamName: item.examName,
		});

		try {
			const examDetails = await fetchExamDetails({
				examId: item.examId,
			});

			await handleSwitchExamContext(examDetails);
			const hasPracticeTests = item.bundleType === "PRACTICE_TEST";
			const url =
				!hasPracticeTests || isMobile ? "/study-pack" : "/practice-tests";
			router.push(url);
			setShowFreeQuizDialog(true);

			trackRedeemCopilotEvent(events.examContextSwitchSucceeded, {
				type: "success",
				message: "Exam context switched successfully",
				switchToExamId: item.examId,
				switchToExamName: item.examName,
			});
		} catch (e) {
			trackRedeemCopilotEvent(events.examContextSwitchFailed, {
				type: "error",
				message: e instanceof Error ? e.message : "Exam context switch failed",
				switchToExamId: item.examId,
				switchToExamName: item.examName,
			});
		}
	};

	return (
		<Card mode="elevated" style={{ overflow: "hidden" }}>
			<Card.Title
				title={success ? "🎉 Redeemed Successfully" : "🚀 Previously Redeemed"}
				titleVariant="titleLarge"
				subtitle="Access your content here"
			/>
			{items.map((item) => {
				return (
					<List.Item
						key={item.bundleId}
						title={<Text variant="titleMedium">{item.examName}</Text>}
						description={
							<Box>
								<Text variant="labelSmall">
									{formatCase(item.bundleType)}
									{item.planLength && ` (${item.planLength})`}
								</Text>
								{item.expiryDate && (
									<Text variant="labelSmall">
										Expires: {new Date(item.expiryDate).toLocaleDateString()}
									</Text>
								)}
							</Box>
						}
						titleNumberOfLines={2}
						onPress={() => handleAccessContent(item)}
						left={(props) => (
							<List.Icon
								icon={() => <ProductIcon image={item.icon} size={48} />}
								{...props}
							/>
						)}
						right={(props) => (
							<List.Icon
								icon={() => (
									<Button
										mode="outlined"
										onPress={() => handleAccessContent(item)}
									>
										<Text variant="labelSmall">Open</Text>
									</Button>
								)}
								{...props}
							/>
						)}
					/>
				);
			})}

			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={() => setShowFreeQuizDialog(false)}
				/>
			)}
		</Card>
	);
};
